// Portuguese (Portugal, formal) copy for /on-demand-ftp. Same shape as copy.en.ts (checked by copy.test.ts).
// "O envio web" is the site's Portuguese name for the web uploader; "FTP a pedido" as on the pricing pages.
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
// "Fotos", not "fotografias" (copy.test.ts keeps every role word out, and that check would catch it).
import type { OnDemandFtpCopy } from "./copy.en";

const pt: OnDemandFtpCopy = {
  seo: {
    title: "Como funciona o FTP a pedido | Photohawk",
    description:
      "Se o FTP for indispensável, qualquer pessoa do seu espaço de trabalho pode iniciar o seu próprio servidor FTP da Photohawk a partir do envio web, em todos os planos. Fica pronto em cerca de 30 segundos, com um endereço pessoal que nunca muda, e desliga-se sozinho após uma hora sem atividade.",
  },
  hero: {
    badge: "Todos os planos",
    eyebrow: "Já disponível",
    title: "Como funciona o FTP a pedido",
    subtitle:
      "Se o FTP for indispensável, inicie o seu próprio servidor FTP a partir do envio web sempre que precisar. Fica pronto em cerca de 30 segundos e desliga-se sozinho quando terminar.",
  },
  before: {
    title: "Antes de começar: o envio web é mais rápido",
    body: "O envio web é a forma padrão de enviar fotos, em todos os planos.",
    points: [
      { id: "parallel", text: "Envia muitas fotos de uma só vez, muitas mais do que uma aplicação de FTP como o FileZilla (2 de cada vez por predefinição, e no máximo 10)." },
      { id: "adaptive", text: "Adapta-se à sua ligação para a aproveitar ao máximo." },
      { id: "live", text: "As pré-visualizações seguem primeiro, para que a sua galeria fique online em minutos enquanto chegam os originais." },
      { id: "resume", text: "Se a ligação falhar, retoma sozinho." },
    ],
    button: "Abrir o envio web",
    link: "Conheça o novo envio web",
  },
  what: {
    heading: "O seu próprio servidor FTP, quando precisar",
    body: "Qualquer pessoa do seu espaço de trabalho pode iniciar o seu próprio servidor FTP a partir do envio web, em todos os planos. Cada pessoa tem o seu próprio servidor, com um endereço pessoal que nunca muda, por isso configura a sua aplicação de FTP uma única vez. Nada fica a funcionar quando não o utiliza: desliga-se sozinho após uma hora sem atividade.",
    date: "1 de novembro de 2026",
    closing: "Os nossos servidores FTP partilhados sempre ativos encerram a 1 de novembro de 2026. Utiliza-os atualmente? Inicie o seu próprio servidor e altere o servidor (host) na sua aplicação de FTP.",
  },
  steps: {
    heading: "Como funciona",
    subtitle: "Quatro passos da primeira vez. Depois, basta iniciar o servidor e enviar.",
    items: [
      { id: "open", title: "Abra o envio web", body: "Aceda a `upload.photohawk.com` e inicie sessão, ou abra o separador «Upload» de uma galeria." },
      { id: "start", title: "Inicie o seu servidor", body: "Clique em «FTP server», no topo, e depois em «Start FTP server». Fica pronto em cerca de 30 segundos." },
      { id: "connect", title: "Copie o endereço e o acesso para a sua aplicação de FTP", body: "O seu endereço, a porta 21, o seu email e a sua palavra-passe da Photohawk. O endereço nunca muda." },
      { id: "upload", title: "Envie as suas fotos", body: "Adicione as fotos à pasta da galeria. Desliga-se após uma hora sem envios." },
    ],
    button: "Iniciar o seu servidor FTP",
    buttonNote: "Abre o envio web com o seu servidor FTP pronto a iniciar.",
  },
  panel: {
    heading: "O que vai ver",
    body: "Está tudo no painel «Your FTP server» do envio web, com um botão para copiar cada dado.",
    points: [
      { id: "address", text: "O seu endereço, como `swift-heron-4821.ftp.photohawk.com`. É seu para sempre, por isso só o configura uma vez." },
      { id: "ip", text: "O endereço IP do servidor também é apresentado. Muda sempre que inicia o servidor, por isso utilize o endereço sempre que possível." },
      { id: "countdown", text: "Uma contagem decrescente até se desligar, com «Keep it running» e «Stop»." },
    ],
    alt: "O painel “Your FTP server” no estado Ready em Europe (Ireland), com o servidor swift-heron-4821.ftp.photohawk.com, o endereço IP, a porta 21, o utilizador alex@example.com, o modo passivo e FTP sobre TLS, e uma contagem decrescente com os botões Keep it running e Stop.",
  },
  details: {
    heading: "Os seus dados de ligação",
    subtitle: "Introduza-os na sua aplicação de FTP.",
    rows: [
      { id: "host", label: "Servidor (host)", value: "O seu endereço, p. ex. `swift-heron-4821.ftp.photohawk.com` (ou o endereço IP apresentado)" },
      { id: "port", label: "Porta", value: "21" },
      { id: "user", label: "Utilizador", value: "O seu email da Photohawk" },
      { id: "password", label: "Palavra-passe", value: "A sua palavra-passe da Photohawk, ou a sua palavra-passe FTP (abaixo)" },
      { id: "mode", label: "Modo de transferência", value: "Passivo" },
      { id: "encryption", label: "Encriptação", value: "FTP simples, ou FTPS (FTP explícito sobre TLS). Para FTPS, utilize o nome do servidor, não o endereço IP." },
    ],
    switching: "Já utilizava o FTP da Photohawk? Mantenha o utilizador, a palavra-passe e as pastas. Basta alterar o servidor (host) para o seu novo endereço.",
  },
  password: {
    heading: "Uma palavra-passe só para FTP, se preferir",
    body: "Em vez de guardar a sua palavra-passe da Photohawk numa aplicação de FTP, clique em «Create FTP password» no painel. Só funciona no seu servidor FTP, com o seu email como utilizador. Pode copiá-la, criar uma nova (a anterior deixa de funcionar em menos de um minuto) ou removê-la a qualquer momento. A sua palavra-passe da Photohawk também continua a funcionar.",
  },
  folders: {
    heading: "As suas pastas",
    body: "Vai ver as mesmas pastas de sempre:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "Uma galeria com uma só coleção é simplesmente `/Galleries/<gallery>`." },
      { id: "workspaces", text: "Envia para mais do que um espaço de trabalho? Cada um tem a sua própria pasta, com as galerias lá dentro." },
      { id: "create", text: "Crie primeiro a galeria e as suas coleções na Photohawk. As fotos aparecem na galeria em instantes, tal como com o envio web." },
    ],
  },
  off: {
    heading: "Desliga-se sozinho",
    body: "O seu servidor desliga-se após 1 hora sem atividade, por isso não tem de se lembrar de nada. O painel mostra uma contagem decrescente.",
    points: [
      { id: "keep", text: "«Keep it running» reinicia a contagem sem enviar nada." },
      { id: "stop", text: "«Stop» desliga-o de imediato quando terminar." },
      { id: "caps", text: "Por segurança, também se desliga após 12 horas sem envios em curso, e no máximo após 24 horas." },
      { id: "again", text: "Volte a iniciá-lo quando quiser, com o mesmo endereço e o mesmo acesso." },
    ],
  },
  regions: {
    heading: "Regiões",
    body: "O seu servidor pode funcionar na Europa (Irlanda), no leste dos EUA (N. Virginia) ou na Ásia-Pacífico (Malásia). Escolhemos a mais próxima por si, e pode alterá-la antes de iniciar.",
  },
  filezilla: {
    heading: "Exemplo: configurar o FileZilla",
    subtitle: "Outras aplicações de FTP utilizam as mesmas definições, por vezes com nomes ligeiramente diferentes.",
    steps: [
      { id: "site", text: "Abra «File → Site Manager» e clique em «New site»." },
      { id: "protocol", text: "Protocolo: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Servidor: o seu endereço, p. ex. `swift-heron-4821.ftp.photohawk.com`. Porta: `21`." },
      { id: "tls", text: "Encriptação: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Tipo de acesso: «Normal». Utilizador: o seu email da Photohawk. Palavra-passe: a sua palavra-passe FTP ou da Photohawk." },
      { id: "passive", text: "No separador «Transfer Settings», escolha «Passive». Clique em «Connect» e abra `Galleries`." },
    ],
    tip: "Vai enviar uma pasta grande? O FileZilla envia 2 de cada vez por predefinição, e no máximo 10. Pode aumentar a definição de transferências simultâneas até 10 («Settings → Transfers»), ou utilizar o envio web, que envia muitas mais de uma só vez.",
  },
  trouble: {
    heading: "Se algo não funcionar",
    items: [
      { id: "connect", title: "Não consigo ligar-me", body: "Confirme que o painel indica «Ready». Se o nome do servidor acabou de ser criado, utilize o endereço IP durante um minuto." },
      { id: "listing", title: "Ligo-me, mas a lista de pastas não carrega", body: "Ative o modo passivo." },
      { id: "logins", title: "“Too many failed logins”", body: "Aguarde 15 minutos e verifique o utilizador (o seu email) e a palavra-passe." },
    ],
  },
  faq: {
    heading: "Perguntas",
    items: [
      {
        q: "Porque é que o meu servidor se desligou?",
        a: "Desliga-se após uma hora sem atividade, para nunca estar a funcionar quando não o utiliza. Volte a iniciá-lo quando quiser: o endereço e o acesso mantêm-se. Para o manter ligado sem enviar nada, clique em «Keep it running». Também se desliga após 12 horas sem envios em curso, e no máximo após 24 horas.",
      },
      {
        q: "Duas pessoas podem ter servidores ao mesmo tempo?",
        a: "Sim. Cada pessoa do seu espaço de trabalho pode iniciar o seu próprio servidor, um cada, com o seu próprio endereço. Só o seu acesso entra no seu servidor.",
      },
      {
        q: "É seguro?",
        a: "Sim. Com FTPS (FTP sobre TLS) no nome do seu servidor, a ligação é encriptada com um certificado de confiança. Só o seu acesso funciona no seu servidor e, após várias tentativas de início de sessão falhadas, o servidor bloqueia essa ligação durante 15 minutos.",
      },
      {
        q: "Tenho de pagar mais?",
        a: "Não. O seu próprio servidor FTP está incluído em todos os planos.",
      },
      {
        q: "Está em todos os planos?",
        a: "Sim, incluindo o Lite. No dia a dia, o envio web ou o Photohawk Studio são mais rápidos; o seu próprio servidor FTP está disponível quando o FTP for indispensável.",
      },
      {
        q: "O que acontece ao antigo FTP partilhado?",
        a: "Os servidores FTP partilhados sempre ativos encerram a 1 de novembro de 2026 e funcionam como sempre até lá. Para continuar a utilizar FTP, inicie o seu próprio servidor e altere o servidor (host) na sua aplicação de FTP para o seu novo endereço. O utilizador, a palavra-passe e as pastas mantêm-se.",
      },
    ],
  },
  cta: {
    heading: "Quando estiver pronto",
    body: "No dia a dia, utilize o envio web. Quando o FTP for indispensável, o seu próprio servidor está à distância de um clique.",
    primary: "Abrir o envio web",
    secondary: "Iniciar o seu servidor FTP",
  },
};

export default pt;
